import React, { useState } from "react";
import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import MemberReportFilters from "../../../MemberReports/components/MemberReportFilters/MemberReportFilters";
import { useMemberReport } from "../../../MemberReports/hooks/useMemberReport";
import { useDownloadMemberReportCsv } from "../../../MemberReports/hooks/useDownloadMemberReportCsv";
import { defaultDateRange } from "../../../MemberReports/utils/defaultDateRange";
import { fetchMyTransactionHistory } from "../../api/fetchMyTransactionHistory/fetchMyTransactionHistoryApi";
import { fetchMyTransactionHistoryCsv } from "../../api/fetchMyTransactionHistory/fetchMyTransactionHistoryCsvApi";
import {
  MyTransactionHistorySortKey,
  MyTransactionItem,
} from "../../api/fetchMyTransactionHistory/types";
import { myTransactionHistoryColumns } from "../MyTransactionHistoryColumns/MyTransactionHistoryColumns";

const columns = myTransactionHistoryColumns();

const SORT_KEYS: readonly MyTransactionHistorySortKey[] = [
  "date",
  "confirmationNumber",
  "eventName",
  "sessionName",
  "type",
  "net",
  "status",
];

/**
 * Member-facing transaction history: the active company's event registration
 * payments and refunds. Authenticated only; the backend scopes the rows to the
 * active company, so there is no permission and no guard here.
 */
const MyTransactionHistoryPage: React.FC = () => {
  const [defaultFilters] = useState(defaultDateRange);

  const {
    items,
    totalCount,
    loading,
    pagination,
    sorting,
    filters,
    exportParams,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useMemberReport<MyTransactionItem, MyTransactionHistorySortKey>({
    fetchPage: fetchMyTransactionHistory,
    sortKeys: SORT_KEYS,
    initialFilters: defaultFilters,
  });

  const { loading: csvLoading, downloadCsv } = useDownloadMemberReportCsv({
    fetchCsv: fetchMyTransactionHistoryCsv,
    fileNamePrefix: "my-transaction-history-report",
  });

  return (
    <MainPageWrapper
      subtitle="Your company's event registration payments and refunds"
      title="Transaction History"
    >
      <MemberReportFilters
        dateLabel="Date"
        defaultFilters={defaultFilters}
        filters={filters}
        isExporting={csvLoading}
        onExportCsv={() => downloadCsv(exportParams)}
        onFilterChange={handleFilterChange}
      />
      <DataTable<MyTransactionItem>
        columns={columns}
        data={items}
        loading={loading}
        noDataMessage="No transactions found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(_row, index) => `${pagination.pageIndex}-${index}`}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default MyTransactionHistoryPage;
